
/* -----------------------------------------------------------------------------------------------------
  - Created: [20-02-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains FutureScheduleTable using MUI components
  ------------------------------------------------------------------------------------------------------- */

import React, { useEffect, useState } from 'react';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination';
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable';
import { get_future_schedule_reports_with_pagination } from '../../../../config/ReportApis/futureScheduleReportApis';
import Loader from '../../../../Components/Loader/Loader';
import { handlePaginationEntries } from '../../../../utils/pagination';
import FutureScheduleFilter from '../FutureScheduleFilter/FutureScheduleFilter';
import useDataExport from '../../../../utils/useDataExport';

function FutureScheduleTable() {
    const [scheduleReportList, setScheduleReportList] = useState([]);

    // Calculate the total pages based on the number of items and items per page
    const [pageNumber, setPageNumber] = useState(1);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [pagination, setPagination] = useState({ start: 0, end: 0, totalEntries: 0 });

    const [filterValues, setFilterValues] = useState({}); // Define filter values state
    const [isLoading, setIsLoading] = useState(false); // Loader state


    // Columns for the table
    const columns = [
        { title: "#", key: "key" },
        { title: "Employee Name", key: "Employee_Name" },
        { title: "Worksite", key: "Worksite" },
        { title: "Survey", key: "Survey" },
        { title: "Schedule Code", key: "Schedule_Code" },
        { title: "From", key: "Date_From" },
        { title: "To", key: "Date_To" },


    ]

    // Map the schedule data into table rows
    const rows = () => scheduleReportList?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        Employee_Name: item.Employee_Name,
        Worksite: item.Worksite,
        Schedule_Code: item?.Schedule_Code,
        Date_From: item.Date_From,
        total_mandays: item?.total_mandays,
        Date_To: item?.Date_To,
        Survey: item?.Survey,

    }));


    const headers = columns.map(col => col.title);

    const mapDataFn = (item, index) => [
        index + 1,
        item?.Employee_Name || "—",
        item?.Worksite || "—",
        item?.Survey || "—",
        item?.Schedule_Code || "—",
        item?.Date_From || "—",
        item?.Date_To || "—",

    ];
    const {
        handleExportExcel,
        handleExportPDF,
        isExportingExcel,
        isExportingPDF,
    } = useDataExport(
        get_future_schedule_reports_with_pagination,
        mapDataFn,
        headers,
        "Employee Future Schedule Report"
    );

    // Fetch schedule data on page change or refresh
    useEffect(() => {
        getFutureScheduleReport();
    }, [pageNumber, totalItemPerPage, filterValues]);


    // Fetch schedule data from API
    const getFutureScheduleReport = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        // Append filter values to formData
        Object.keys(filterValues).forEach(key => formData.append(key, filterValues[key]));

        try {
            setIsLoading(true)
            const response = await get_future_schedule_reports_with_pagination(formData);
            if (response?.status === "success") {
                const data = response?.data;
                setScheduleReportList(data?.data);
                setTotalPages(data.last_page);
                setPagination(handlePaginationEntries(data.from, data.to, data.total));
            } else {
                setScheduleReportList([]);
            }
        } catch (error) {

        } finally {
            setIsLoading(false)
        }
    };

    // Handle search filter change
    const handleSearchFilter = (filterValues) => {
        setFilterValues(filterValues);
    };

    // Handle pagination changes
    const handlePageChange = (pageNumber) => setPageNumber(pageNumber);
    const handleItemPageCountChange = (pageItemCount) => setTotalItemPerPage(pageItemCount);


    return (
        <div className='report-table-container'>
            <FutureScheduleFilter
                tableTitle="Employee Future Schedule Report"
                searchFilter={handleSearchFilter}

                on_change_value={handlePageChange}
                on_page_count_update={handleItemPageCountChange}

                onExportPDF={() => handleExportPDF(filterValues, { page: 1, pageItemCount: pagination?.totalEntries })}
                onExportExcel={() => handleExportExcel(filterValues, { page: 1, pageItemCount: pagination?.totalEntries })}
                isExportingPDF={isExportingPDF}
                isExportingExcel={isExportingExcel}


            />
            <div className="table-container">
                <CustomTable
                    columns={columns}
                    rows={isLoading ? [] : rows()}
                    isCheckbox={false}
                    isLoading={isLoading}
                />
            </div>
            <div className='pagination-container'>
                {isLoading ? <Loader /> : <CustomBottomPagination
                    items_per_page={totalItemPerPage}
                    current_page={pageNumber}
                    total_page_count={totalPages}
                    on_change_value={handlePageChange}
                    on_page_count_update={handleItemPageCountChange}
                    pagination={pagination}
                />}
            </div>
        </div>
    );
}

export default FutureScheduleTable;
